import React, { useEffect, useRef } from "react";
import { motion } from "framer-motion";

// A single clip that only plays while it is on screen, so we never decode
// nine videos at once — keeps scrolling smooth. Falls back to a still.
function Clip({ clip, index }) {
  const ref = useRef(null);

  useEffect(() => {
    const v = ref.current;
    if (!v || !clip.video) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          const p = v.play();
          if (p && p.catch) p.catch(() => {});
        } else {
          v.pause();
        }
      },
      { threshold: 0.25 }
    );
    io.observe(v);
    return () => io.disconnect();
  }, [clip.video]);

  return (
    <motion.div
      data-testid={`motion-clip-${index}`}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.7, delay: index * 0.06, ease: [0.25, 1, 0.5, 1] }}
      className="group relative overflow-hidden rounded-2xl border border-white/10 bg-ink-800 aspect-[4/5]"
    >
      {clip.video ? (
        <video
          ref={ref}
          className="absolute inset-0 h-full w-full object-cover faded-media"
          muted loop playsInline preload="none" poster={clip.poster || undefined}
        >
          <source src={clip.video} type="video/mp4" />
        </video>
      ) : (
        <motion.img
          src={clip.poster}
          alt={clip.label || "Movement clip"}
          loading="lazy"
          className="absolute inset-0 h-full w-full object-cover faded-media"
          animate={{ scale: [1, 1.08, 1] }}
          transition={{ duration: 14 + index * 2, repeat: Infinity, ease: "easeInOut" }}
        />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-ink-900/85 via-ink-900/10 to-ink-900/20" />
      <div className="absolute inset-x-0 bottom-0 p-4">
        <p className="text-white text-sm font-medium leading-snug">{clip.label}</p>
      </div>
    </motion.div>
  );
}

export function MotionClips({ clips = [] }) {
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5" data-testid="motion-clips">
      {clips.map((c, i) => (
        <Clip key={c.label + i} clip={c} index={i} />
      ))}
    </div>
  );
}

export default MotionClips;
